import { memo, useCallback, useState } from "react";
import FilterAltRoundedIcon from "@mui/icons-material/FilterAltRounded";
import { Box, IconButton, Tooltip, Typography, useTheme } from "@mui/material";
import { IFilter, IFiltersProps } from "./models";
import { Modal } from "@4uhub/lib4uhub";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";

const TRANSLATION_PATH = "components.panel.filters_modal.";

export const Filters = memo(({ filter }: IFiltersProps) => {
  const { t } = useTranslation();

  const theme = useTheme();

  const [open, setOpen] = useState(false);

  const appliedFilters = JSON.parse(filter);

  const openHandler = useCallback(() => {
    setOpen(true);
  }, []);

  const closeHandler = useCallback(() => {
    setOpen(false);
  }, []);

  return (
    <>
      <Modal
        title={`${t(`${TRANSLATION_PATH}filters`)}`}
        open={open}
        onClose={closeHandler}
      >
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.4, ease: [0.6, -0.05, 0.01, 0.99] }}
          style={{
            display: "flex",
            flexDirection: "column",
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: 10,
          }}
        >
          {Object.entries(appliedFilters).flatMap(([key, filter], idx, arr) => {
            const item = filter as IFilter;
            const isFirst = idx === 0;
            const isLast = idx === arr.length - 1;

            return (
              <Box key={key}>
                <Box
                  sx={(t) => ({
                    px: 2,
                    py: 1,
                    backgroundColor:
                      t.palette.grey[t.palette.mode === "light" ? 200 : 800],
                    borderTopLeftRadius: isFirst ? 10 : 0,
                    borderTopRightRadius: isFirst ? 10 : 0,
                  })}
                >
                  <Typography>{item.name}</Typography>
                </Box>
                {item.data.map((d, index) => (
                  <Box
                    sx={(t) => ({
                      py: 1,
                      px: 2,
                      borderBottom:
                        index < item.data.length - 1
                          ? `1px solid ${t.palette.divider}`
                          : undefined,
                      transition: "all 0.2s ease-in-out",
                      borderBottomLeftRadius: isLast ? 10 : 0,
                      borderBottomRightRadius: isLast ? 10 : 0,
                      "&:hover": {
                        backgroundColor:
                          t.palette.grey[
                            t.palette.mode === "light" ? 100 : 900
                          ],
                      },
                    })}
                    key={d.id}
                  >
                    <Typography>{d.name}</Typography>
                  </Box>
                ))}
              </Box>
            );
          })}
        </motion.div>
      </Modal>
      <Tooltip title={t(`${TRANSLATION_PATH}tooltip`)}>
        <IconButton size="small" onClick={openHandler}>
          <FilterAltRoundedIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    </>
  );
});
